import React, { useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Navbar } from "./components/Navbar";
import { Footer } from "./components/Footer";
import { ChatBot } from "./components/ChatBot";
import { CookieConsent, initTracking } from "./components/CookieConsent";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { AuthProvider } from "./context/AuthContext";
import { AuthModal } from "./components/auth/AuthModal";

// Code-split: lazy load all pages for better performance
const HomePage = lazy(() => import("./pages/HomePage"));
const PropertiesPage = lazy(() => import("./pages/PropertiesPage"));
const PropertyDetailPage = lazy(() => import("./pages/PropertyDetailPage"));
const ComparePage = lazy(() => import("./pages/ComparePage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const EMICalculatorPage = lazy(() => import("./pages/EMICalculatorPage"));
const RentalYieldCalculatorPage = lazy(() => import("./pages/RentalYieldCalculatorPage"));
const BlogPage = lazy(() => import("./pages/BlogPage"));
const BlogDetailPage = lazy(() => import("./pages/BlogDetailPage"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const AdminResetPassword = lazy(() => import("./pages/AdminResetPassword"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const CareersPage = lazy(() => import("./pages/CareersPage"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const TermsOfServicePage = lazy(() => import("./pages/TermsOfServicePage"));
const WishlistPage = lazy(() => import("./pages/WishlistPage"));
const ReportPage = lazy(() => import("./pages/ReportPage"));
const LoyaltyPage = lazy(() => import("./pages/LoyaltyPage"));
const LuxuryAdvisorsPage = lazy(() => import("./pages/LuxuryAdvisorsPage"));
const CommercialPropertyPage = lazy(() => import("./pages/CommercialPropertyPage"));
const NRIPropertyPage = lazy(() => import("./pages/NRIPropertyPage"));
const DubaiAdvisoryPage = lazy(() => import("./pages/DubaiAdvisoryPage"));
const AreaPropertyPage = lazy(() => import("./pages/AreaPropertyPage"));
const BudgetSearchPage = lazy(() => import("./pages/BudgetSearchPage"));
const SeoLandingRouter = lazy(() => import("./components/SeoLandingRouter"));
const AccountPage = lazy(() => import("./pages/AccountPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));

// Minimal loading fallback
const PageLoader = () => (
  <div className="min-h-screen bg-obsidian flex items-center justify-center">
    <div className="w-8 h-8 border-2 border-brand/30 border-t-brand rounded-full animate-spin" />
  </div>
);

function AppContent() {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');

  // Scroll to top on route change & track page
  useEffect(() => {
    window.scrollTo(0, 0);
    initTracking(location.pathname);
  }, [location.pathname]);

  // Content protection — disable right-click & keyboard shortcuts on public pages
  useEffect(() => {
    if (isAdminRoute) {
      document.body.classList.add('admin-body');
      return () => document.body.classList.remove('admin-body');
    }
    document.body.classList.remove('admin-body');

    const blockContext = (e) => e.preventDefault();
    const blockKeys = (e) => {
      if (
        (e.ctrlKey && (e.key === 'u' || e.key === 's' || e.key === 'p' || e.key === 'c' || e.key === 'a')) ||
        (e.ctrlKey && e.shiftKey && (e.key === 'I' || e.key === 'J' || e.key === 'C')) ||
        e.key === 'F12'
      ) {
        e.preventDefault();
      }
    };

    document.addEventListener('contextmenu', blockContext);
    document.addEventListener('keydown', blockKeys);
    return () => {
      document.removeEventListener('contextmenu', blockContext);
      document.removeEventListener('keydown', blockKeys);
    };
  }, [isAdminRoute]);

  return (
    <div className="App min-h-screen bg-obsidian">
      {!isAdminRoute && <Navbar />}
      {/* Subtle watermark on public pages */}
      {!isAdminRoute && (
        <div className="pointer-events-none fixed inset-0 z-[9999] overflow-hidden select-none" aria-hidden="true" style={{ opacity: 0.015 }}>
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="absolute whitespace-nowrap font-outfit font-bold text-white text-base tracking-[0.4em] uppercase"
              style={{ top: `${i * 220 + 60}px`, left: `${(i % 2) * 80 - 40}px`, transform: 'rotate(-35deg)', transformOrigin: '0 0', width: '300%' }}>
              PROPASSIST.IN &nbsp;&nbsp;&nbsp;&nbsp; PROPASSIST.IN &nbsp;&nbsp;&nbsp;&nbsp; PROPASSIST.IN &nbsp;&nbsp;&nbsp;&nbsp; PROPASSIST.IN
            </div>
          ))}
        </div>
      )}
      <main>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/properties" element={<PropertiesPage />} />
            <Route path="/properties/:id" element={<PropertyDetailPage />} />
            <Route path="/compare" element={<ComparePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/emi-calculator" element={<EMICalculatorPage />} />
            <Route path="/rental-yield-calculator" element={<RentalYieldCalculatorPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:slug" element={<BlogDetailPage />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/reset-password" element={<AdminResetPassword />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/careers" element={<CareersPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/terms-of-service" element={<TermsOfServicePage />} />
            <Route path="/wishlist" element={<WishlistPage />} />
            <Route path="/report/:reportId" element={<ReportPage />} />
            <Route path="/loyalty" element={<LoyaltyPage />} />
            <Route path="/luxury-advisors-pune" element={<LuxuryAdvisorsPage />} />
            <Route path="/commercial-property-pune" element={<CommercialPropertyPage />} />
            <Route path="/nri-property-investment" element={<NRIPropertyPage />} />
            <Route path="/dubai-property-advisory" element={<DubaiAdvisoryPage />} />
            <Route path="/account" element={<AccountPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="*" element={<SeoLandingRouter />} />
          </Routes>
        </Suspense>
      </main>
      {!isAdminRoute && <Footer />}
      {!isAdminRoute && <ChatBot />}
      {!isAdminRoute && <CookieConsent />}
      <AuthModal />
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <BrowserRouter>
          <AuthProvider>
            <AppContent />
          </AuthProvider>
        </BrowserRouter>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
